import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Permission } from '@Types/permission';
import { stableStringify } from '@Utils/searchParams';

type PermissionsResponse = {
  data: Permission[];
  meta: Record<string, any>;
};
type DefaultError = unknown;

type UsePermissionsParams<S extends Record<string, any> = Record<string, any>> =
  {
    /** Optional query string params to forward to service */
    searchParams?: S;
    /** Override default enabled behavior */
    enabled?: boolean;
  };

export function usePermissions<
  TData = PermissionsResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UsePermissionsParams = {},
  options?: Omit<
    UseQueryOptions<PermissionsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<PermissionsResponse, TError, TData>({
    queryKey: ['permissions', stableParams],
    enabled: enabled,
    queryFn: async () => {
      return service.getPermissions(searchParams ?? {});
    },
    // caller can pass select, staleTime, gcTime, retry, refetchOnWindowFocus, etc.
    ...(options as any),
  });
}
